import { supabase } from '../config/supabase';

const DEV = import.meta.env.DEV;

/* ─── Helpers ─── */

/**
 * Normalize an incoming routes array into the JSONB shape stored on
 * club_events: [{ route_id, route_slug, label }]. Drops entries without a
 * slug and trims labels. Accepts either the DB shape (route_id/route_slug)
 * or the form shape (routeId/routeSlug).
 */
function normalizeRoutes(routes) {
  if (!Array.isArray(routes)) return [];
  return routes
    .map(r => ({
      route_id: r.route_id ?? r.routeId ?? null,
      route_slug: r.route_slug ?? r.routeSlug ?? null,
      label: (r.label ?? '').toString().trim(),
    }))
    .filter(r => r.route_slug);
}

/**
 * Resolve the event visibility. Prefers an explicit 3-way value; falls back
 * to the legacy boolean isPublic when visibility isn't provided.
 */
function normalizeVisibility(visibility, isPublic) {
  if (visibility === 'link_only' || visibility === 'members' || visibility === 'public') {
    return visibility;
  }
  if (isPublic === false) return 'link_only';
  return 'members';
}

/* ─── Event CRUD ─── */

/**
 * Create a new event for a club.
 */
export async function createEvent(data, userId) {
  try {
    // Normalize multi-route legs and keep the legacy single-route columns in
    // sync with the first leg for backwards compatibility.
    const routes = normalizeRoutes(data.routes);
    const firstLeg = routes[0] || null;
    // visibility ('link_only' | 'members' | 'public') is the source of truth;
    // is_public is derived so older code paths keep working.
    const visibility = normalizeVisibility(data.visibility, data.isPublic);

    const row = {
      club_id: data.clubId,
      created_by: userId,
      title: data.title,
      description: data.description || null,
      event_date: data.eventDate,
      end_date: data.endDate || null,
      event_time: data.eventTime || null,
      end_time: data.endTime || null,
      location_text: data.locationText || null,
      banner_url: data.bannerUrl || null,
      max_attendees: data.maxAttendees || null,
      registration_type: data.registrationType || 'person',
      is_paid: data.isPaid || false,
      price_cents: data.priceCents || 0,
      allow_guests: data.allowGuests || false,
      max_guests_per_registrant: data.maxGuestsPerRegistrant || 1,
      routes,
      route_id: firstLeg?.route_id || null,
      route_slug: firstLeg?.route_slug || null,
      visibility,
      is_public: visibility !== 'link_only',
      registration_required: data.registrationRequired !== false,
      status: data.status || 'published',
      notify_admins_on_registration: data.notifyAdminsOnRegistration || false,
      registration_opens_at: data.registrationOpensAt || null,
      registration_closes_at: data.registrationClosesAt || null,
    };

    const { data: event, error } = await supabase
      .from('club_events')
      .insert(row)
      .select()
      .single();

    if (error) {
      if (DEV) console.error('[Event] Create error:', error.message);
      return { data: null, error: error.message };
    }

    if (DEV) console.log('[Event] Created:', event.id);
    return { data: event, error: null };
  } catch (err) {
    if (DEV) console.error('[Event] Create exception:', err.message);
    return { data: null, error: err.message };
  }
}

/**
 * Update an existing event.
 */
export async function updateEvent(eventId, fields) {
  try {
    const row = {};
    if (fields.title !== undefined) row.title = fields.title;
    if (fields.description !== undefined) row.description = fields.description;
    if (fields.eventDate !== undefined) row.event_date = fields.eventDate;
    if (fields.endDate !== undefined) row.end_date = fields.endDate;
    if (fields.eventTime !== undefined) row.event_time = fields.eventTime;
    if (fields.endTime !== undefined) row.end_time = fields.endTime;
    if (fields.locationText !== undefined) row.location_text = fields.locationText;
    if (fields.bannerUrl !== undefined) row.banner_url = fields.bannerUrl;
    if (fields.maxAttendees !== undefined) row.max_attendees = fields.maxAttendees;
    if (fields.registrationType !== undefined) row.registration_type = fields.registrationType;
    if (fields.isPaid !== undefined) row.is_paid = fields.isPaid;
    if (fields.priceCents !== undefined) row.price_cents = fields.priceCents;
    if (fields.allowGuests !== undefined) row.allow_guests = fields.allowGuests;
    if (fields.maxGuestsPerRegistrant !== undefined) row.max_guests_per_registrant = fields.maxGuestsPerRegistrant;
    // Multi-route legs. Writing `routes` also re-syncs the legacy single-route
    // columns to the first leg so older consumers stay correct.
    if (fields.routes !== undefined) {
      const routes = normalizeRoutes(fields.routes);
      const firstLeg = routes[0] || null;
      row.routes = routes;
      row.route_id = firstLeg?.route_id || null;
      row.route_slug = firstLeg?.route_slug || null;
    } else {
      if (fields.routeId !== undefined) row.route_id = fields.routeId;
      if (fields.routeSlug !== undefined) row.route_slug = fields.routeSlug;
    }
    // Visibility is the source of truth; keep is_public derived from it.
    if (fields.visibility !== undefined) {
      row.visibility = normalizeVisibility(fields.visibility, fields.isPublic);
      row.is_public = row.visibility !== 'link_only';
    } else if (fields.isPublic !== undefined) {
      row.is_public = fields.isPublic;
      row.visibility = fields.isPublic ? 'members' : 'link_only';
    }
    if (fields.registrationRequired !== undefined) row.registration_required = !!fields.registrationRequired;
    if (fields.status !== undefined) row.status = fields.status;
    if (fields.notifyAdminsOnRegistration !== undefined) row.notify_admins_on_registration = fields.notifyAdminsOnRegistration;
    if (fields.registrationOpensAt !== undefined) row.registration_opens_at = fields.registrationOpensAt;
    if (fields.registrationClosesAt !== undefined) row.registration_closes_at = fields.registrationClosesAt;
    row.updated_at = new Date().toISOString();

    if (Object.keys(row).length <= 1) return { data: null, error: null }; // only updated_at

    const { data, error } = await supabase
      .from('club_events')
      .update(row)
      .eq('id', eventId)
      .select()
      .single();

    if (error) {
      if (DEV) console.error('[Event] Update error:', error.message);
      return { data: null, error: error.message };
    }

    if (DEV) console.log('[Event] Updated:', eventId);
    return { data, error: null };
  } catch (err) {
    if (DEV) console.error('[Event] Update exception:', err.message);
    return { data: null, error: err.message };
  }
}

/**
 * Delete an event. CAUTION: hard-deleting club_events cascades to
 * event_registrations (ON DELETE CASCADE). Once paid registrations
 * exist, we refuse the operation and tell the admin to Cancel Event
 * instead — that path preserves payment history and triggers
 * notifications + refunds.
 *
 * We only allow hard-delete for events with zero paid registrations,
 * which in practice means drafts and brand-new events that no one
 * has bought a ticket for yet.
 */
export async function deleteEvent(eventId) {
  try {
    // Refuse the delete if there are any non-cancelled paid registrations.
    // status='cancelled' rows are residue from refunds / unregister and
    // are safe to discard alongside the event.
    const { count: paidCount, error: countErr } = await supabase
      .from('event_registrations')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('payment_status', 'paid')
      .neq('status', 'cancelled');

    if (countErr) {
      if (DEV) console.error('[Event] Delete preflight count failed:', countErr.message);
      return { error: countErr.message };
    }

    if (paidCount && paidCount > 0) {
      const msg = paidCount === 1
        ? 'This event has 1 paid registration. Use "Cancel Event" instead so the registrant is notified and (optionally) refunded.'
        : `This event has ${paidCount} paid registrations. Use "Cancel Event" instead so registrants are notified and (optionally) refunded.`;
      if (DEV) console.warn('[Event] Refusing delete:', msg);
      return { error: msg };
    }

    const { error } = await supabase
      .from('club_events')
      .delete()
      .eq('id', eventId);

    if (error) {
      if (DEV) console.error('[Event] Delete error:', error.message);
      return { error: error.message };
    }

    if (DEV) console.log('[Event] Deleted:', eventId);
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] Delete exception:', err.message);
    return { error: err.message };
  }
}

/* ─── Event Queries (RPCs) ─── */

/**
 * Get all events for a club (admin view — includes draft/cancelled).
 */
export async function getClubEventsAdmin(clubId) {
  try {
    const { data, error } = await supabase
      .rpc('get_club_events_admin', { club_uuid: clubId });

    if (error) {
      if (DEV) console.error('[Event] getClubEventsAdmin error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getClubEventsAdmin exception:', err.message);
    return [];
  }
}

/**
 * Get upcoming events across all user's clubs (member-facing feed).
 */
export async function getUserUpcomingEvents(userId) {
  try {
    const { data, error } = await supabase
      .rpc('get_user_upcoming_events', { uid: userId });

    if (error) {
      if (DEV) console.error('[Event] getUserUpcomingEvents error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getUserUpcomingEvents exception:', err.message);
    return [];
  }
}

/**
 * Get events happening today where the user is registered but NOT checked in.
 * Used for the Dashboard check-in banner.
 */
export async function getUserTodayCheckinEvents(userId) {
  try {
    const { data, error } = await supabase
      .rpc('get_user_today_checkin_events', { uid: userId });

    if (error) {
      if (DEV) console.error('[Event] getUserTodayCheckinEvents error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getUserTodayCheckinEvents exception:', err.message);
    return [];
  }
}

/**
 * Get single event detail with user's registration status.
 */
export async function getEventDetail(eventId, userId) {
  try {
    const { data, error } = await supabase
      .rpc('get_event_detail', { event_uuid: eventId, uid: userId });

    if (error) {
      if (DEV) console.error('[Event] getEventDetail error:', error.message);
      return null;
    }

    // RPC returns array, we want the first row
    const row = Array.isArray(data) ? data[0] : data;
    return row || null;
  } catch (err) {
    if (DEV) console.error('[Event] getEventDetail exception:', err.message);
    return null;
  }
}

/**
 * Get attendee list for an event (admin view).
 */
export async function getEventAttendees(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_event_attendees', { event_uuid: eventId });

    if (error) {
      if (DEV) console.error('[Event] getEventAttendees error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getEventAttendees exception:', err.message);
    return [];
  }
}

/* ─── Registration ─── */

/**
 * Register user for an event.
 * @param {string} eventId
 * @param {string} userId
 * @param {string|null} vehicleId - for car-type events
 * @param {string|null} vehicleLabel - denormalized label e.g. "2019 Porsche 911"
 */
export async function registerForEvent(eventId, userId, vehicleId = null, vehicleLabel = null) {
  try {
    const row = {
      event_id: eventId,
      user_id: userId,
      vehicle_id: vehicleId,
      vehicle_label: vehicleLabel,
      status: 'registered',
    };

    const { data, error } = await supabase
      .from('event_registrations')
      .insert(row)
      .select()
      .single();

    if (error) {
      // Duplicate key — might be a cancelled registration that can be reactivated
      if (error.code === '23505') {
        const { data: reactivated, error: reErr } = await supabase
          .from('event_registrations')
          .update({ status: 'registered', cancelled_at: null, vehicle_id: vehicleId, vehicle_label: vehicleLabel })
          .eq('event_id', eventId)
          .eq('user_id', userId)
          .eq('status', 'cancelled')
          .select()
          .maybeSingle();

        if (reactivated) {
          if (DEV) console.log('[Event] Re-registered (reactivated cancelled):', eventId);
          return { data: reactivated, error: null };
        }

        if (DEV) console.log('[Event] Already registered:', eventId);
        return { data: null, error: 'Already registered for this event' };
      }
      if (DEV) console.error('[Event] Register error:', error.message);
      return { data: null, error: error.message };
    }

    if (DEV) console.log('[Event] Registered:', userId, 'for', eventId);
    return { data, error: null };
  } catch (err) {
    if (DEV) console.error('[Event] Register exception:', err.message);
    return { data: null, error: err.message };
  }
}

/**
 * Unregister user from an event (soft delete — sets status to 'cancelled').
 */
export async function unregisterFromEvent(eventId, userId) {
  try {
    const { data, error } = await supabase
      .from('event_registrations')
      .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .neq('status', 'cancelled')
      .select('id')
      .single();

    if (error) {
      if (DEV) console.error('[Event] Unregister error:', error.message);
      return { error: error.message, registrationId: null };
    }

    if (DEV) console.log('[Event] Unregistered:', userId, 'from', eventId);
    return { error: null, registrationId: data?.id || null };
  } catch (err) {
    if (DEV) console.error('[Event] Unregister exception:', err.message);
    return { error: err.message, registrationId: null };
  }
}

/**
 * Check in to an event (marks status as checked_in).
 */
export async function checkInToEvent(registrationId) {
  try {
    const { error } = await supabase
      .from('event_registrations')
      .update({
        status: 'checked_in',
        checked_in_at: new Date().toISOString(),
      })
      .eq('id', registrationId);

    if (error) {
      if (DEV) console.error('[Event] Check-in error:', error.message);
      return { error: error.message };
    }

    if (DEV) console.log('[Event] Checked in:', registrationId);
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] Check-in exception:', err.message);
    return { error: err.message };
  }
}

/* ─── Ticketed Registration (RPC) ─── */

/**
 * Register with ticket type and addons via atomic RPC.
 * For free ticketed events (total = $0). Paid orders go through Stripe.
 */
export async function registerWithTicket(eventId, userId, ticketTypeId, {
  vehicleId = null,
  vehicleLabel = null,
  guestCount = 0,
  guestNames = null,
  addonItems = [],
  answers = [],
} = {}) {
  try {
    const { data, error } = await supabase
      .rpc('register_for_event_with_ticket', {
        p_event_id: eventId,
        p_user_id: userId,
        p_ticket_type_id: ticketTypeId,
        p_vehicle_id: vehicleId,
        p_vehicle_label: vehicleLabel,
        p_guest_count: guestCount,
        p_guest_names: guestNames,
        p_addon_items: addonItems, // [{id, qty}]
        p_answers: answers, // [{question_id, answer_values}]
      });

    if (error) {
      if (DEV) console.error('[Event] registerWithTicket error:', error.message);
      // Handle specific RPC exceptions
      if (error.message.includes('sold out') || error.message.includes('Sold out')) {
        return { data: null, error: 'This ticket is sold out' };
      }
      if (error.message.includes('already registered') || error.code === '23505') {
        return { data: null, error: 'Already registered for this event' };
      }
      return { data: null, error: error.message };
    }

    if (DEV) console.log('[Event] Registered with ticket:', data);
    return { data, error: null }; // data = registration_id UUID
  } catch (err) {
    if (DEV) console.error('[Event] registerWithTicket exception:', err.message);
    return { data: null, error: err.message };
  }
}

/* ─── Ticket Types ─── */

/**
 * Get ticket types with sold counts for an event.
 */
export async function getEventTicketTypes(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_event_ticket_types', { p_event_id: eventId });

    if (error) {
      if (DEV) console.error('[Event] getEventTicketTypes error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getEventTicketTypes exception:', err.message);
    return [];
  }
}

/**
 * Save ticket types for an event (upsert existing, insert new, delete removed).
 * @param {string} eventId
 * @param {Array} ticketTypes - [{id?, name, description, priceCents, isFree, quantityAvailable, sortOrder}]
 */
export async function saveEventTicketTypes(eventId, ticketTypes) {
  try {
    // Get existing ticket types to diff
    const { data: existing } = await supabase
      .from('event_ticket_types')
      .select('id')
      .eq('event_id', eventId);

    const existingIds = (existing || []).map(t => t.id);
    const incomingIds = ticketTypes.filter(t => t.id).map(t => t.id);

    // Delete removed ticket types
    const toDelete = existingIds.filter(id => !incomingIds.includes(id));
    if (toDelete.length > 0) {
      const { error: delErr } = await supabase
        .from('event_ticket_types')
        .delete()
        .in('id', toDelete);
      if (delErr && DEV) console.error('[Event] Delete ticket types error:', delErr.message);
    }

    // Upsert each ticket type
    for (let i = 0; i < ticketTypes.length; i++) {
      const tt = ticketTypes[i];
      const row = {
        event_id: eventId,
        name: tt.name,
        description: tt.description || null,
        price_cents: tt.isFree ? 0 : (tt.priceCents || 0),
        is_free: !!tt.isFree,
        quantity_available: tt.quantityAvailable || null,
        sort_order: i,
      };

      if (tt.id) {
        // Update existing
        const { error } = await supabase
          .from('event_ticket_types')
          .update(row)
          .eq('id', tt.id);
        if (error && DEV) console.error('[Event] Update ticket type error:', error.message);
      } else {
        // Insert new
        const { error } = await supabase
          .from('event_ticket_types')
          .insert(row);
        if (error && DEV) console.error('[Event] Insert ticket type error:', error.message);
      }
    }

    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] saveEventTicketTypes exception:', err.message);
    return { error: err.message };
  }
}

/* ─── Add-ons ─── */

/**
 * Get addons with sold counts for an event.
 */
export async function getEventAddons(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_event_addons', { p_event_id: eventId });

    if (error) {
      if (DEV) console.error('[Event] getEventAddons error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getEventAddons exception:', err.message);
    return [];
  }
}

/**
 * Save addons for an event (upsert existing, insert new, delete removed).
 * @param {string} eventId
 * @param {Array} addons - [{id?, name, description, priceCents, maxPerRegistrant, quantityAvailable, sortOrder}]
 */
export async function saveEventAddons(eventId, addons) {
  try {
    // Get existing addons to diff
    const { data: existing } = await supabase
      .from('event_addons')
      .select('id')
      .eq('event_id', eventId);

    const existingIds = (existing || []).map(a => a.id);
    const incomingIds = addons.filter(a => a.id).map(a => a.id);

    // Delete removed addons
    const toDelete = existingIds.filter(id => !incomingIds.includes(id));
    if (toDelete.length > 0) {
      const { error: delErr } = await supabase
        .from('event_addons')
        .delete()
        .in('id', toDelete);
      if (delErr && DEV) console.error('[Event] Delete addons error:', delErr.message);
    }

    // Upsert each addon
    for (let i = 0; i < addons.length; i++) {
      const a = addons[i];
      const row = {
        event_id: eventId,
        name: a.name,
        description: a.description || null,
        price_cents: a.priceCents || 0,
        max_per_registrant: a.maxPerRegistrant || 10,
        quantity_available: a.quantityAvailable || null,
        sort_order: i,
      };

      if (a.id) {
        const { error } = await supabase
          .from('event_addons')
          .update(row)
          .eq('id', a.id);
        if (error && DEV) console.error('[Event] Update addon error:', error.message);
      } else {
        const { error } = await supabase
          .from('event_addons')
          .insert(row);
        if (error && DEV) console.error('[Event] Insert addon error:', error.message);
      }
    }

    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] saveEventAddons exception:', err.message);
    return { error: err.message };
  }
}

/**
 * Get registration items for a specific registration (admin view).
 */
export async function getRegistrationItems(registrationId) {
  try {
    const { data, error } = await supabase
      .rpc('get_registration_items', { p_registration_id: registrationId });

    if (error) {
      if (DEV) console.error('[Event] getRegistrationItems error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getRegistrationItems exception:', err.message);
    return [];
  }
}

/* ─── Questionnaire (Event Questions & Registration Answers) ─── */

/**
 * Get all questionnaire questions for an event, sorted.
 */
export async function getEventQuestions(eventId) {
  try {
    const { data, error } = await supabase
      .from('event_questions')
      .select('id, event_id, question_text, question_type, options, is_required, sort_order')
      .eq('event_id', eventId)
      .order('sort_order', { ascending: true });

    if (error) {
      if (DEV) console.error('[Event] getEventQuestions error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getEventQuestions exception:', err.message);
    return [];
  }
}

/**
 * Upsert questionnaire questions for an event.
 * @param {string} eventId
 * @param {Array<{id?:string, questionText:string, questionType:'text'|'dropdown'|'multiple_choice', options:string[], isRequired:boolean}>} questions
 */
export async function saveEventQuestions(eventId, questions) {
  try {
    const { data: existing } = await supabase
      .from('event_questions')
      .select('id')
      .eq('event_id', eventId);

    const existingIds = (existing || []).map(q => q.id);
    const incomingIds = questions.filter(q => q.id).map(q => q.id);

    // Delete removed
    const toDelete = existingIds.filter(id => !incomingIds.includes(id));
    if (toDelete.length > 0) {
      const { error: delErr } = await supabase
        .from('event_questions')
        .delete()
        .in('id', toDelete);
      if (delErr && DEV) console.error('[Event] Delete questions error:', delErr.message);
    }

    // Upsert each
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const row = {
        event_id: eventId,
        question_text: (q.questionText || '').trim(),
        question_type: q.questionType || 'text',
        options: Array.isArray(q.options) ? q.options.filter(o => (o || '').trim().length > 0) : [],
        is_required: !!q.isRequired,
        sort_order: i,
      };

      if (!row.question_text) continue; // skip empty

      if (q.id) {
        const { error } = await supabase
          .from('event_questions')
          .update(row)
          .eq('id', q.id);
        if (error && DEV) console.error('[Event] Update question error:', error.message);
      } else {
        const { error } = await supabase
          .from('event_questions')
          .insert(row);
        if (error && DEV) console.error('[Event] Insert question error:', error.message);
      }
    }

    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] saveEventQuestions exception:', err.message);
    return { error: err.message };
  }
}

/**
 * Get questionnaire answers for a specific registration (admin/own-user view).
 * Returns rows: { question_id, question_text, question_type, answer_values, sort_order }
 */
export async function getRegistrationAnswers(registrationId) {
  try {
    const { data, error } = await supabase
      .rpc('get_registration_answers', { p_registration_id: registrationId });

    if (error) {
      if (DEV) console.error('[Event] getRegistrationAnswers error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getRegistrationAnswers exception:', err.message);
    return [];
  }
}

/**
 * Save pending questionnaire answers for a paid event after Stripe checkout completes.
 * Finds the caller's most recent non-cancelled registration for the event.
 */
export async function saveAnswersForRegistration(eventId, answers) {
  try {
    const { data, error } = await supabase
      .rpc('save_answers_for_registration', {
        p_event_id: eventId,
        p_answers: answers,
      });

    if (error) {
      if (DEV) console.error('[Event] saveAnswersForRegistration error:', error.message);
      return { error: error.message };
    }
    return { data, error: null };
  } catch (err) {
    if (DEV) console.error('[Event] saveAnswersForRegistration exception:', err.message);
    return { error: err.message };
  }
}

/* ─── Route Picker (queries rm_routes) ─── */

/**
 * Get published routes owned by a user (for admin event form route picker).
 */
export async function getAvailableRoutes(userId) {
  try {
    const { data, error } = await supabase
      .from('rm_routes')
      .select('id, title, slug')
      .eq('owner_id', userId)
      .eq('is_published', true)
      .order('title', { ascending: true });

    if (error) {
      // rm_routes table may not exist — fail silently
      if (DEV) console.error('[Event] getAvailableRoutes error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getAvailableRoutes exception:', err.message);
    return [];
  }
}

/* ─── Refunds ─── */

/**
 * Issue a full refund for a paid event registration (admin-only).
 * Calls the refund-event-registration edge function.
 */
export async function refundRegistration(registrationId, eventId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refund-event-registration`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ registrationId, eventId }),
      },
    );

    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Refund failed');
    return result;
  } catch (err) {
    if (DEV) console.error('[Event] refundRegistration error:', err.message);
    throw err;
  }
}

/**
 * Cancel an event, optionally auto-refund paid registrations, and notify registrants.
 * @param {string} eventId
 * @param {'auto_refund' | 'manual_refund' | 'no_refund'} refundOption
 * @returns {{ refunded: number, notified: boolean }}
 */
export async function cancelEvent(eventId, refundOption = 'no_refund') {
  try {
    // 1. Mark event as cancelled
    const { error: updateErr } = await supabase
      .from('club_events')
      .update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancellation_refund_option: refundOption,
      })
      .eq('id', eventId);

    if (updateErr) throw updateErr;

    // 2. Auto-refund paid registrations if requested
    let refunded = 0;
    if (refundOption === 'auto_refund') {
      const { data: paidRegs } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('event_id', eventId)
        .eq('payment_status', 'paid')
        .neq('status', 'cancelled');

      if (paidRegs && paidRegs.length > 0) {
        for (const reg of paidRegs) {
          try {
            await refundRegistration(reg.id, eventId);
            refunded++;
          } catch (refundErr) {
            if (DEV) console.error('[Event] Auto-refund failed for reg', reg.id, refundErr.message);
          }
        }
      }
    }

    // 3. Notify registrants via edge function
    let notified = false;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-event-cancelled-notification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ event_id: eventId, refund_option: refundOption }),
          },
        );
        notified = true;
      }
    } catch (notifyErr) {
      if (DEV) console.error('[Event] Cancel notification failed:', notifyErr.message);
    }

    return { refunded, notified };
  } catch (err) {
    if (DEV) console.error('[Event] cancelEvent error:', err.message);
    throw err;
  }
}

/**
 * User requests a refund on their own registration.
 * Sets refund_requested_at and refund_request_message.
 */
export async function requestRefund(eventId, message) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('event_registrations')
      .update({
        refund_requested_at: new Date().toISOString(),
        refund_request_message: (message || '').slice(0, 500),
      })
      .eq('event_id', eventId)
      .eq('user_id', user.id)
      .eq('payment_status', 'paid')
      .neq('status', 'cancelled');

    if (error) throw error;
    return { success: true };
  } catch (err) {
    if (DEV) console.error('[Event] requestRefund error:', err.message);
    throw err;
  }
}

/**
 * Admin declines a refund request — silently clears the request fields.
 * User can re-request later.
 */
export async function declineRefundRequest(registrationId) {
  try {
    const { error } = await supabase
      .from('event_registrations')
      .update({
        refund_requested_at: null,
        refund_request_message: null,
      })
      .eq('id', registrationId);

    if (error) throw error;
    return { success: true };
  } catch (err) {
    if (DEV) console.error('[Event] declineRefundRequest error:', err.message);
    throw err;
  }
}

/* ─── Public Event Access (no auth required) ─── */

/**
 * Get public event details — no membership check, no user-specific data.
 * Works for unauthenticated users viewing shared event links.
 */
export async function getPublicEventDetail(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_public_event_detail', { event_uuid: eventId });

    if (error) {
      if (DEV) console.error('[Event] getPublicEventDetail error:', error.message);
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    return row || null;
  } catch (err) {
    if (DEV) console.error('[Event] getPublicEventDetail exception:', err.message);
    return null;
  }
}

/**
 * Get ticket types for a public event — no membership check.
 */
export async function getPublicEventTicketTypes(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_public_event_ticket_types', { p_event_id: eventId });

    if (error) {
      if (DEV) console.error('[Event] getPublicEventTicketTypes error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getPublicEventTicketTypes exception:', err.message);
    return [];
  }
}

/* ─── Platform Admin: All Events ─── */

/**
 * Get all events across all clubs (platform owner only).
 */
export async function getAllEventsAdmin() {
  try {
    const { data, error } = await supabase.rpc('admin_all_events');
    if (error) {
      if (DEV) console.error('[Event] getAllEventsAdmin error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getAllEventsAdmin exception:', err.message);
    return [];
  }
}

/* ─── Platform Admin: Blacklist ─── */

/**
 * Get all blacklisted emails (platform owner only).
 */
export async function getBlacklist() {
  try {
    const { data, error } = await supabase.rpc('admin_get_blacklist');
    if (error) {
      if (DEV) console.error('[Event] getBlacklist error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getBlacklist exception:', err.message);
    return [];
  }
}

/**
 * Add email to blacklist (platform owner only).
 */
export async function addToBlacklist(email, reason) {
  try {
    const { error } = await supabase.rpc('admin_add_to_blacklist', {
      p_email: email,
      p_reason: reason || null,
    });
    if (error) {
      if (DEV) console.error('[Event] addToBlacklist error:', error.message);
      return { error: error.message };
    }
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] addToBlacklist exception:', err.message);
    return { error: err.message };
  }
}

/**
 * Remove email from blacklist (platform owner only).
 */
export async function removeFromBlacklist(email) {
  try {
    const { error } = await supabase.rpc('admin_remove_from_blacklist', {
      p_email: email,
    });
    if (error) {
      if (DEV) console.error('[Event] removeFromBlacklist error:', error.message);
      return { error: error.message };
    }
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Event] removeFromBlacklist exception:', err.message);
    return { error: err.message };
  }
}

/**
 * Check if an email is blacklisted.
 */
export async function isBlacklisted(email) {
  try {
    const { data, error } = await supabase.rpc('check_event_blacklist', {
      p_email: email,
    });
    if (error) {
      if (DEV) console.error('[Event] isBlacklisted error:', error.message);
      return false;
    }
    return !!data;
  } catch (err) {
    if (DEV) console.error('[Event] isBlacklisted exception:', err.message);
    return false;
  }
}

/* ─── Admin Notifications ─── */

/**
 * Fire-and-forget: invoke edge function to email club admins about a new registration.
 * Safe to call even if notifications are disabled — the edge function checks the flag.
 */
export async function sendRegistrationNotification(eventId, registrationId, registrantName, registrantEmail) {
  try {
    const { error } = await supabase.functions.invoke('send-registration-notification', {
      body: { eventId, registrationId, registrantName, registrantEmail },
    });
    if (error) {
      if (DEV) console.error('[Event] Notification send failed:', error);
    } else {
      if (DEV) console.log('[Event] Registration notification sent for', eventId);
    }
  } catch (err) {
    if (DEV) console.error('[Event] Notification invoke failed:', err.message);
  }
}

/**
 * Fire-and-forget: capture a failed registration attempt into
 * registration_failures so an admin can review + add manually. Called
 * from the client when the register RPC/insert raised something we
 * don't classify as a legitimate rejection (sold out / already
 * registered). Also emails club admins if their event opted in.
 */
export async function logRegistrationFailure(payload) {
  try {
    const { error } = await supabase.functions.invoke('log-registration-failure', {
      body: payload,
    });
    if (error && DEV) console.error('[Event] logRegistrationFailure failed:', error);
  } catch (err) {
    if (DEV) console.error('[Event] logRegistrationFailure invoke failed:', err.message);
  }
}

/**
 * Given an error surfaced by the register RPC/insert, decide whether
 * it's a legitimate user-facing rejection (sold out, already registered)
 * or an unexpected failure that should fall into the safety-net queue.
 */
export function isLegitimateRegistrationError(errorMessage) {
  if (!errorMessage) return false;
  const m = errorMessage.toLowerCase();
  return (
    m.includes('sold out') ||
    m.includes('already registered') ||
    m.includes('ticket type not found') ||
    m.includes('add-on not found') ||
    m.includes('add-on sold out') ||
    m.includes('exceeded max quantity')
  );
}

export async function sendCancellationNotification(eventId, registrationId, registrantName, registrantEmail) {
  try {
    const { error } = await supabase.functions.invoke('send-event-cancelled-notification', {
      body: { eventId, registrationId, registrantName, registrantEmail },
    });
    if (error) {
      if (DEV) console.error('[Event] Cancellation notification send failed:', error);
    } else {
      if (DEV) console.log('[Event] Cancellation notification sent for', eventId);
    }
  } catch (err) {
    if (DEV) console.error('[Event] Cancellation notification invoke failed:', err.message);
  }
}

/**
 * Get addons for a public event — no membership check.
 */
export async function getPublicEventAddons(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_public_event_addons', { p_event_id: eventId });

    if (error) {
      if (DEV) console.error('[Event] getPublicEventAddons error:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Event] getPublicEventAddons exception:', err.message);
    return [];
  }
}
